/** 与客户端语言码对齐。6fdb54d、c20f61a。 */
export const APP_LANGS = ['en', 'ar', 'tr', 'hi', 'en-IN', 'bn', 'ur', 'es'] as const

export type AppLang = (typeof APP_LANGS)[number]

/** 仓库内有静态文案包的语言 */
export const STATIC_LANGS = ['en', 'ar', 'tr'] as const

export type StaticLang = (typeof STATIC_LANGS)[number]

/** antd-mobile 本仓库实际接入的语言 */
export const ANTD_LANGS = ['en', 'ar', 'tr', 'es'] as const

export type AntdLang = (typeof ANTD_LANGS)[number]

const APP_LANG_SET = new Set<string>(APP_LANGS)
const STATIC_LANG_SET = new Set<string>(STATIC_LANGS)

/** 大小写不敏感查找表：en-in → en-IN */
const APP_LANG_BY_LOWER = new Map<string, AppLang>(
  APP_LANGS.map((lang) => [lang.toLowerCase(), lang])
)

export function isAppLang(lang: string): lang is AppLang {
  return APP_LANG_SET.has(lang)
}

export function isStaticLang(lang: string): lang is StaticLang {
  return STATIC_LANG_SET.has(lang)
}

/**
 * 尝试把任意语言码映射到白名单；无法映射时返回 null（供系统语言探测跳过）。
 * - trim、_ → -
 * - 先大小写不敏感精确匹配（保留 en-IN）
 * - 再取主语言码（ar-SA → ar，en-US → en）
 */
export function tryMapAppLang(lang: string | null | undefined): AppLang | null {
  if (!lang) return null
  const trimmed = lang.trim()
  if (!trimmed) return null

  const normalized = trimmed.replace(/_/g, '-')
  const lower = normalized.toLowerCase()

  const exact = APP_LANG_BY_LOWER.get(lower)
  if (exact) return exact

  const primary = lower.split('-')[0]
  if (!primary) return null
  return APP_LANG_BY_LOWER.get(primary) ?? null
}

/** 未知语言码统一回退英文；保留 en-IN 等原始码 */
export function normalizeAppLang(lang: string | null | undefined): AppLang {
  return tryMapAppLang(lang) ?? 'en'
}

/**
 * 从浏览器/WebView 语言列表探测 App 语言。
 * 按 navigator.languages 顺序取第一个能映射到白名单的项，再退 navigator.language。
 */
export function detectSystemLang(): AppLang {
  if (typeof navigator === 'undefined') return 'en'

  const candidates: string[] = []
  if (Array.isArray(navigator.languages)) {
    candidates.push(...navigator.languages)
  }
  if (navigator.language) {
    candidates.push(navigator.language)
  }

  for (const candidate of candidates) {
    const mapped = tryMapAppLang(candidate)
    if (mapped) return mapped
  }
  return 'en'
}

/**
 * 本地静态基线语言。
 * 新语言（hi/bn/ur/es/en-IN）无本地包时用英文作安全基线；
 * 远程请求仍使用原始 lang，切勿把 en-IN 提前归一成 en。
 */
export function getStaticBaseLang(lang: AppLang): StaticLang {
  return isStaticLang(lang) ? lang : 'en'
}

/** RTL 语言（阿拉伯语、乌尔都语）启用 mirror */
const RTL_LANGS = new Set<AppLang>(['ar', 'ur'])

export function shouldEnableRtl(lang: AppLang): boolean {
  return RTL_LANGS.has(lang)
}

/**
 * antd-mobile locale 选择：
 * 有对应包用对应包，否则回退英文（hi/bn/ur/en-IN）。
 */
export function getAntdLang(lang: AppLang): AntdLang {
  if (lang === 'ar' || lang === 'tr' || lang === 'es') return lang
  return 'en'
}

export function deepMerge<T extends Record<string, any>>(
  base: T,
  override: Record<string, any>
): T {
  const output: Record<string, any> = {
    ...base
  }

  Object.keys(override).forEach((key) => {
    const baseValue = output[key]
    const overrideValue = override[key]

    if (
      baseValue &&
      typeof baseValue === 'object' &&
      !Array.isArray(baseValue) &&
      overrideValue &&
      typeof overrideValue === 'object' &&
      !Array.isArray(overrideValue)
    ) {
      output[key] = deepMerge(baseValue, overrideValue)
      return
    }

    output[key] = overrideValue
  })

  return output as T
}

type LocaleBundle = { translation: Record<string, any> }

/**
 * 为当前语言创建 i18n resources：
 * - 始终用静态基线（en/ar/tr）克隆，避免 locales[lang] 为 undefined
 * - 远程文案成功时深合并覆盖基线；远程没有的 key 保留本地句子
 */
export function createI18nResources(
  lang: AppLang,
  staticLocales: Record<StaticLang, LocaleBundle>,
  remoteLocales?: Record<string, any>
): Record<string, LocaleBundle> {
  const baseLang = getStaticBaseLang(lang)
  const baseTranslation = {
    ...staticLocales[baseLang].translation
  }
  const translation = remoteLocales ? deepMerge(baseTranslation, remoteLocales) : baseTranslation

  return {
    ...staticLocales,
    [lang]: {
      translation
    }
  }
}
